/**
 * PeoplePage — /people (v2-Relaunch, People-Merge)
 *
 * Issue #51: die Identitaets-Aufloesung Contact+Host laeuft ueber
 * GET /api/v2/people (Api::V2::PeopleController / PeopleMergeService,
 * Backend). Vorher mergte dieser Screen clientseitig ausschliesslich ueber
 * E-Mail (SPEC.md Global decisions, 2026-09-01) — die bei den meisten
 * Datensaetzen fehlt, wodurch fast jede Person doppelt erschien (einmal
 * Contact, einmal Host). Der Server-Merge nutzt E-Mail (case-insensitiv),
 * sonst normalisierten Namen als Fallback; das FE haengt an jede gemergte
 * Zeile nur noch Hotspot-Anzahl/Saldo per host_id an.
 *
 * Wallet-Abgleich (vom SPEC ebenfalls genannt) ist NICHT umgesetzt: Contact-
 * und Host-Wallet-Felder sind fachlich verschiedene Adressformate
 * (helium_wallet vs. solana_wallet_address) ohne verlaessliche
 * 1:1-Beziehung im Contract.
 *
 * ponytail: "Open balance"/"Total paid" werden durch Summieren der bereits
 * geladenen Settlements je host_id gebildet (kein Server-Aggregat je Host
 * vorhanden, siehe hosts/useHosts.ts). Bei mehreren Waehrungen je Host wird
 * nur die zuletzt gesehene Waehrung angezeigt und alles darauf aufsummiert
 * (seltener Fall) — Upgrade: Betrag je Waehrung aufschluesseln, falls das in
 * der Praxis auftritt.
 *
 * Bekannte Luecken (siehe Ergebnis-Bericht):
 *  - CSV-Import/Export deckt nur Contacts ab (people-Endpunkt liefert keine
 *    Vollstammdaten); Hosts sind nicht Teil des Exports.
 *  - "+ Add person" legt einen Contact an (CreateContactModal, unveraendert)
 *    - Host-Anlage mit Hotspot-Zuweisung ist ein eigener Flow auf /hosts.
 *  - Klick auf eine Zeile fuehrt zur Person-Detail-Seite /people/:id (Contact-
 *    ID; merged Host wird dort per E-Mail aufgeloest, siehe ContactDetailPage
 *    useMatchedHost). Reine Host-Zeilen ohne Contact-Merge fuehren weiterhin
 *    zu /hosts/:id — eigene Host-only-Detailseite ist nicht Teil dieses
 *    Auftrags (person-detail-Fix).
 *  - Anreicherung (Hotspot-Anzahl/Saldo) laedt nur die erste Seite der
 *    Regeln/Settlements (per_page=100, siehe hosts/useHosts.ts) — bei mehr
 *    Eintraegen fehlen einzelne Werte.
 */
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { Upload, Download, ChevronRight } from 'lucide-react'
import { Avatar } from '../../components/Avatar'
import { formatNumber } from '../../components/ui/format'
import { useContacts } from './useContacts'
import { usePeople } from './usePeople'
import type { PersonDto } from './usePeople'
import {
  useHostSplitRules,
  useHostsSettlements,
  buildHostRowData,
} from '../hosts/useHosts'
import type { Settlement } from '../hosts/useHosts'
import { CreateContactModal } from './CreateContactModal'
import { HostFormDrawer } from '../hosts/HostFormDrawer'
import { ContactsImportModal } from './ContactsImportModal'
import { exportContactsToCsv } from './contactsCsv'
import './people.css'

type RoleFilter = '' | 'host' | 'contact'

type Balance = { amount: number; currency: string }

type Person = {
  key: string
  name: string
  email: string | null
  roles: Array<'host' | 'contact'>
  contactId?: number
  hostId?: number
  hotspotCount: number
  openBalance: Balance | null
  totalPaid: Balance | null
}

// --- Helpers -----------------------------------------------------------------

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('') || '?'
}

/**
 * Summiert die geladenen Settlements je Host in offen (draft/approved) vs. bezahlt (settled).
 *
 * Issue #129: Ein Host, der bislang nur in einer der beiden Kategorien
 * Settlements hat, bekam fuer die andere Kategorie mangels Waehrungs-Info
 * `null` ("keine Daten") statt `0` ("nichts offen/bezahlt") — in derselben
 * Tabelle stand dadurch je nach Host mal "0.00 HNT" mal "—" fuer dieselbe
 * Semantik "nichts vorhanden". Ein Host hat immer einen (ggf. 0-)Saldo in
 * beiden Kategorien, daher hier immer eine Balance mit Betrag liefern; die
 * Waehrung der jeweils anderen Kategorie bzw. HNT (Domain-Default, siehe
 * ContactDetailPage.tsx) dient nur als Anzeige-Fallback, wenn diese Kategorie
 * gar keine Settlements hat.
 */
function hostBalances(hostId: number, settlements: Settlement[] | undefined): { open: Balance; paid: Balance } {
  let openAmount = 0
  let openCurrency: string | undefined
  let paidAmount = 0
  let paidCurrency: string | undefined

  for (const s of settlements ?? []) {
    if (s.host_id !== hostId) continue
    const amount = Number(s.total_amount ?? '0')
    if (!Number.isFinite(amount)) continue
    if (s.status === 'settled') {
      paidAmount += amount
      paidCurrency = s.currency ?? paidCurrency
    } else {
      openAmount += amount
      openCurrency = s.currency ?? openCurrency
    }
  }

  const fallbackCurrency = openCurrency ?? paidCurrency ?? 'HNT'
  return {
    open: { amount: openAmount, currency: openCurrency ?? fallbackCurrency },
    paid: { amount: paidAmount, currency: paidCurrency ?? fallbackCurrency },
  }
}

/**
 * Reichert die serverseitig bereits identitaets-gemergte Liste
 * (GET /api/v2/people, Issue #51) um Hotspot-Anzahl/Saldo an, die nur
 * client-seitig aus Regeln+Settlements je host_id berechnet werden koennen
 * (kein Server-Aggregat dafuer, siehe hosts/useHosts.ts).
 */
function mergePeople(
  people: PersonDto[],
  hotspotCounts: Map<number, number>,
  settlements: Settlement[] | undefined,
): Person[] {
  return people
    .map((p) => {
      const hostId = p.host_id ?? undefined
      const { open, paid } = hostId !== undefined ? hostBalances(hostId, settlements) : { open: null, paid: null }
      const roles = (p.roles ?? []) as Array<'host' | 'contact'>
      return {
        key: p.contact_id != null ? `contact-${p.contact_id}` : `host-${p.host_id}`,
        name: p.name ?? '',
        email: p.email ?? null,
        roles,
        contactId: p.contact_id ?? undefined,
        hostId,
        hotspotCount: hostId !== undefined ? (hotspotCounts.get(hostId) ?? 0) : 0,
        openBalance: open,
        totalPaid: paid,
      }
    })
    .sort((a, b) => a.name.localeCompare(b.name))
}

function formatBalance(b: Balance | null, noValue: string): string {
  if (!b) return noValue
  return `${formatNumber(b.amount, 2)} ${b.currency}`
}

// --- States --------------------------------------------------------------

function LoadingSkeleton() {
  const { t } = useTranslation('contacts')
  return (
    <div className="card" aria-busy="true" aria-label={t('peoplePage.loadingAriaLabel')}>
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="people-row skeleton" aria-hidden="true">
          <div className="row" style={{ gap: 10 }}>
            <div className="skeleton-line" style={{ width: 36, height: 36, borderRadius: '50%' }} />
            <div>
              <div className="skeleton-line" style={{ width: 120, height: 14 }} />
              <div className="skeleton-line" style={{ width: 90, height: 11, marginTop: 4 }} />
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation('contacts')
  return (
    <div className="error-state" role="alert">
      <p>{t('peoplePage.errorLoad')}</p>
      <button className="btn btn-primary" onClick={onRetry}>
        {t('common:actions.retry')}
      </button>
    </div>
  )
}

function EmptyState({ hasSearch }: { hasSearch: boolean }) {
  const { t } = useTranslation('contacts')
  return (
    <div className="empty-state" data-testid="people-empty">
      <p>{hasSearch ? t('peoplePage.empty.noResults') : t('peoplePage.empty.noPeople')}</p>
    </div>
  )
}

// --- Row -------------------------------------------------------------------

function PersonRow({ person }: { person: Person }) {
  const { t } = useTranslation('contacts')
  const noValue = t('peoplePage.noValue')
  // Contact-ID hat Vorrang: /people/:id zeigt die Person-Detail-Seite, die
  // einen verknuepften Host per E-Mail selbst aufloest (ContactDetailPage
  // useMatchedHost). Nur reine Host-Zeilen ohne Contact-Merge landen auf
  // /hosts/:id.
  const to = person.contactId !== undefined ? '/people/$id' : '/hosts/$id'
  const params = { id: String(person.contactId !== undefined ? person.contactId : person.hostId) }

  return (
    <Link className="people-row" to={to} params={params} data-testid={`person-row-${person.key}`}>
      <div className="row" style={{ gap: 10, minWidth: 0 }}>
        <Avatar initials={getInitials(person.name)} size={36} />
        <div style={{ minWidth: 0 }}>
          <div className="ellipsis" style={{ fontWeight: 500 }}>{person.name}</div>
          {person.email && <div className="ellipsis" style={{ fontSize: 11, color: 'var(--text-subtle)' }}>{person.email}</div>}
        </div>
      </div>
      <span>
        {person.roles.includes('host') && (
          <span className="pill pill-primary">{t('peoplePage.roles.host')}</span>
        )}{' '}
        {person.roles.includes('contact') && !person.roles.includes('host') && (
          <span className="pill">{t('peoplePage.roles.contact')}</span>
        )}
      </span>
      <span className="people-col-md" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
        {person.hotspotCount > 0
          ? t(person.hotspotCount === 1 ? 'peoplePage.hotspotCount_one' : 'peoplePage.hotspotCount_other', { count: person.hotspotCount })
          : noValue}
      </span>
      <span className="people-num people-col-md">{formatBalance(person.openBalance, noValue)}</span>
      <span className="people-num">{formatBalance(person.totalPaid, noValue)}</span>
      <span style={{ color: 'var(--text-subtle)' }} aria-hidden="true">
        <ChevronRight size={14} />
      </span>
      <div className="people-mobile-meta">
        {t('peoplePage.mobileMeta', {
          hotspots: person.hotspotCount,
          open: formatBalance(person.openBalance, noValue),
          paid: formatBalance(person.totalPaid, noValue),
        })}
      </div>
    </Link>
  )
}

// --- Main --------------------------------------------------------------

export function ContactsPage() {
  const { t } = useTranslation('contacts')
  const [role, setRole] = useState<RoleFilter>('')
  const [search, setSearch] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  // /hosts leitet auf /people um (SPEC.md Z.10) — die Host-Anlage (inkl.
  // Hotspot-Zuordnung) haengt deshalb hier am zweiten Button.
  const [createHostOpen, setCreateHostOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)

  // `contacts` wird nur noch fuer den CSV-Export gebraucht (voller
  // Contact-Datensatz) — die Identitaets-Zeilen selbst kommen aus usePeople.
  const { contacts, isLoading: contactsLoading, isError: contactsError, refetch: refetchContacts } = useContacts({ perPage: 100 })
  const { people: peopleDto, isLoading: peopleLoading, isError: peopleError, refetch: refetchPeople } = usePeople()
  const { rules } = useHostSplitRules()
  const { settlements, isLoading: settlementsLoading } = useHostsSettlements()

  // Solange die Settlements noch laden, wuerde hostBalances() fuer jeden Host
  // ein sicheres "0,00 HNT" zeigen — ein Betrag, der noch gar nicht bekannt ist.
  // Deshalb gehoert der Ladezustand der Settlements mit ins Gate (wie in
  // ContactDetailPage.tsx), nicht nur Kontakte und Personen.
  const isLoading = contactsLoading || peopleLoading || settlementsLoading
  const isError = contactsError || peopleError

  const hotspotCounts = useMemo(() => {
    const rowData = buildHostRowData(rules, settlements)
    const counts = new Map<number, number>()
    for (const [hostId, data] of rowData) counts.set(hostId, data.hotspotIds.length)
    return counts
  }, [rules, settlements])

  const people = useMemo(
    () => mergePeople(peopleDto ?? [], hotspotCounts, settlements),
    [peopleDto, hotspotCounts, settlements],
  )

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return people.filter((p) => {
      // Wie im Prototyp (people.html): "Hosts" = hat Host-Rolle, "Contacts
      // only" = hat sie NICHT (nicht "hat Contact-Rolle" — sonst waeren
      // Personen mit beiden Rollen in beiden Filtern).
      const matchesRole = !role || (role === 'host' ? p.roles.includes('host') : !p.roles.includes('host'))
      const matchesQuery = !q || p.name.toLowerCase().includes(q) || (p.email ?? '').toLowerCase().includes(q)
      return matchesRole && matchesQuery
    })
  }, [people, role, search])

  return (
    <main className="people-page">
      <header className="page-header" style={{ alignItems: 'flex-end', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1 style={{ margin: '0 0 4px', fontSize: 22 }}>{t('peoplePage.title')}</h1>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', maxWidth: 640 }}>
            {t('peoplePage.subtitle')}
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setImportOpen(true)}
            data-testid="import-people-btn"
          >
            <Upload size={16} aria-hidden="true" />
            {t('peoplePage.importBtn')}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => { if (contacts) exportContactsToCsv(contacts) }}
            disabled={!contacts || contacts.length === 0}
            data-testid="export-people-btn"
          >
            <Download size={16} aria-hidden="true" />
            {t('peoplePage.exportBtn')}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setCreateHostOpen(true)}
            data-testid="create-host-btn"
          >
            {t('peoplePage.addHostBtn')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setCreateOpen(true)}
            data-testid="create-person-btn"
          >
            {t('peoplePage.addBtn')}
          </button>
        </div>
      </header>

      <div className="row" role="group" aria-label={t('peoplePage.filterAriaLabel')} style={{ gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        {(['', 'host', 'contact'] as RoleFilter[]).map((r) => (
          <button
            key={r || 'all'}
            type="button"
            className={`people-fchip${role === r ? ' is-on' : ''}`}
            aria-pressed={role === r}
            onClick={() => setRole(r)}
            data-testid={`role-chip-${r || 'all'}`}
          >
            {t(`peoplePage.chips.${r || 'all'}`)}
          </button>
        ))}
        <input
          className="input"
          type="search"
          placeholder={t('peoplePage.searchPlaceholder')}
          aria-label={t('peoplePage.searchAriaLabel')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 220, marginLeft: 'auto' }}
          data-testid="people-search-input"
        />
      </div>

      <section aria-label={t('peoplePage.listAriaLabel')}>
        {isLoading && <LoadingSkeleton />}
        {isError && !isLoading && <ErrorState onRetry={() => { refetchContacts(); refetchPeople() }} />}
        {!isLoading && !isError && filtered.length === 0 && <EmptyState hasSearch={!!search} />}
        {!isLoading && !isError && filtered.length > 0 && (
          <div className="card" data-testid="people-list">
            <div className="people-row people-head">
              <span>{t('peoplePage.columns.person')}</span>
              <span>{t('peoplePage.columns.roles')}</span>
              <span className="people-col-md">{t('peoplePage.columns.hotspotsHosted')}</span>
              <span className="people-num people-col-md">{t('peoplePage.columns.openBalance')}</span>
              <span className="people-num">{t('peoplePage.columns.totalPaid')}</span>
              <span />
            </div>
            {filtered.map((person) => (
              <PersonRow key={person.key} person={person} />
            ))}
          </div>
        )}
      </section>

      <CreateContactModal isOpen={createOpen} onClose={() => setCreateOpen(false)} />
      <HostFormDrawer open={createHostOpen} onClose={() => setCreateHostOpen(false)} />
      <ContactsImportModal isOpen={importOpen} onClose={() => setImportOpen(false)} />
    </main>
  )
}
